import React from 'react';
import { createPortal } from 'react-dom';

interface MappingProgressSpinnerProps {
    /** Show while a background run is in progress. */
    active: boolean;
    /** Items processed so far (run-status `count`). */
    processed: number | null;
    /** Total items to process this run (run-status `total`). */
    total: number | null;
}

/**
 * Fixed bottom-right progress chip for a background AI-mapping run:
 * spinner + percentage (and "X of Y" when known). Until the backend reports
 * a total, it shows an indeterminate "Starting…" state.
 */
export const MappingProgressSpinner: React.FC<MappingProgressSpinnerProps> = ({
    active,
    processed,
    total,
}) => {
    if (!active) return null;

    const hasTotal = typeof total === 'number' && total > 0;
    const done = processed ?? 0;
    const pct = hasTotal ? Math.min(100, Math.round((done / (total as number)) * 100)) : null;

    return createPortal(
        <div
            role="status"
            aria-live="polite"
            className="mapper-ui font-sans fixed right-6 bottom-6 z-[9990] min-w-[240px] max-w-[320px] flex items-center gap-3 px-4 py-[14px] rounded-2xl bg-mapper-white shadow-mapper-modal border border-mapper-teal-border"
        >
            <div className="relative w-11 h-11 shrink-0 flex items-center justify-center">
                <svg className="absolute inset-0 animate-spin text-mapper-teal" width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <circle cx="22" cy="22" r="18" stroke="currentColor" strokeOpacity="0.18" strokeWidth="3.5" />
                    <path
                        d="M40 22a18 18 0 0 1-18 18"
                        stroke="currentColor"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                    />
                </svg>
                <span className="text-[11px] font-bold text-mapper-teal-hover tabular-nums">
                    {pct !== null ? `${pct}%` : '…'}
                </span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-mapper-text leading-tight">
                    {hasTotal ? 'Generating mappings' : 'Starting AI mapping…'}
                </div>
                <div className="text-[12px] text-mapper-muted-2 mt-[3px] tabular-nums">
                    {hasTotal ? `${done} of ${total}` : 'Preparing…'}
                </div>
                <div className="mt-[8px] h-[5px] rounded-full bg-mapper-teal-bg overflow-hidden">
                    <div
                        className={`h-full rounded-full bg-mapper-teal ${hasTotal ? 'transition-all duration-500' : 'animate-pulse'}`}
                        style={{ width: pct !== null ? `${pct}%` : '40%' }}
                    />
                </div>
            </div>
        </div>,
        document.body
    );
};
